import { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Box,
  Typography,
  Link,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
} from "@mui/material";
import { useAddUserArrayMutation } from "@/store/api/adminApi";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

const ImportMultiUsers = ({ open, onClose, onSucess }) => {
  const [file, setFile] = useState(null);
  const [defaultPassword, setDefaultPassword] = useState("");
  const [defaultClassCode, setDefaultClassCode] = useState("");
  const [importResult, setImportResult] = useState(null);
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [addUserArray, { isLoading }] = useAddUserArrayMutation();

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleImport = async () => {
    if (!file) {
      toast.error("Please select a file to import.");
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const students = XLSX.utils
          .sheet_to_json(worksheet)
          .filter((row) => row["Full name"])
          .map((row) => ({
            firstName: row["Full name"]?.split(" ")[0] || "",
            lastName: row["Full name"]?.split(" ").slice(1).join(" ") || "",
            classCode: row["Class"],
            email: row["Email"],
            username: row["User name"],
            password: row["Password"].toString(),
            role: row["Role"].toLowerCase() || "student",
          }));
        console.log("Students:", students);
        const res = await addUserArray({
          students,
          defaultPassword,
          defaultClassCode,
        }).unwrap();
        const summary = res?.data?.summary;
        const errors = res?.data?.errors || [];

        setImportResult({
          summary,
          errors,
          message: res?.message,
        });

        if (summary?.created > 0) {
          toast.success(
            `Successfully imported ${summary.created} out of ${summary.total} users.`
          );
        }

        if (summary?.failed > 0) {
          setShowResultDialog(true);
        } else {
          onSucess();
          onClose();
        }
      } catch (error) {
        console.error("Failed to import users:", error);
        toast.error(error.data?.message || "Failed to import users.");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleDownloadTemplate = () => {
    const templateData = [
      {
        "Full name": "John Doe",
        Class: "C001",
        Email: "john.doe@example.com",
        "User name": "johndoe",
        Password: "password123",
        Role: "student",
      },
    ];
    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Students");
    XLSX.writeFile(workbook, "student_import_template.xlsx");
  };

  const handleCloseResultDialog = () => {
    setShowResultDialog(false);
    setImportResult(null);
    onSucess();
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Import Multiple Students</DialogTitle>
        <DialogContent>
          <Box sx={{ my: 2 }}>
            <Typography variant="body2" gutterBottom>
              Upload an Excel file with student information. The columns should
              be: Full name, Class, Email, User name, Password, Role.
            </Typography>
            <Link
              component="button"
              variant="body2"
              onClick={handleDownloadTemplate}
            >
              Download Template
            </Link>
          </Box>
          <TextField
            type="file"
            fullWidth
            onChange={handleFileChange}
            inputProps={{ accept: ".xlsx, .xls" }}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={handleImport} variant="contained" disabled={isLoading}>
            {isLoading ? "Importing..." : "Import"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showResultDialog}
        onClose={handleCloseResultDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Import Results</DialogTitle>
        <DialogContent>
          {importResult && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                <AlertTitle>Summary</AlertTitle>
                <Typography variant="body2">
                  Total: <strong>{importResult.summary?.total || 0}</strong> |
                  Created: <strong>{importResult.summary?.created || 0}</strong> |
                  Failed: <strong>{importResult.summary?.failed || 0}</strong>
                </Typography>
              </Alert>

              {importResult.errors && importResult.errors.length > 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom color="error">
                    Failed Imports ({importResult.errors.length})
                  </Typography>
                  <List sx={{ maxHeight: 400, overflow: "auto" }}>
                    {importResult.errors.map((error, index) => (
                      <Box key={index}>
                        <ListItem
                          alignItems="flex-start"
                          sx={{
                            bgcolor: "error.lighter",
                            mb: 1,
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "error.light",
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  mb: 0.5,
                                }}
                              >
                                <Chip
                                  label={`#${index + 1}`}
                                  size="small"
                                  color="error"
                                />
                                <Typography variant="subtitle2">
                                  {error.firstName} {error.lastName}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Box sx={{ mt: 1 }}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mb: 0.5 }}
                                >
                                  <strong>Username:</strong> {error.username}
                                  {error.email && (
                                    <>
                                      {" | "}
                                      <strong>Email:</strong> {error.email}
                                    </>
                                  )}
                                  {error.classCode && (
                                    <>
                                      {" | "}
                                      <strong>Class:</strong> {error.classCode}
                                    </>
                                  )}
                                </Typography>
                                <Alert severity="error" sx={{ mt: 1 }}>
                                  <Typography variant="body2">
                                    <strong>Error:</strong> {error.error}
                                  </Typography>
                                </Alert>
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < importResult.errors.length - 1 && (
                          <Divider sx={{ my: 1 }} />
                        )}
                      </Box>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResultDialog} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ImportMultiUsers;
